'use client'

import { motion } from 'motion/react'
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from 'lucide-react'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'

const linkSets = {
  fr: {
    sections: {
      Produits: ["Huiles d'Olive"],
      Entreprise: ['Nos Produits', 'Notre Histoire', 'Certifications', 'Témoignages', 'Contact'],
      Support: ['FAQ', 'Livraison', 'Retours', 'Conditions', 'Confidentialité', 'Suppression données'],
    },
    navAnchors: {
      'Nos Produits': 'products',
      'Notre Histoire': 'story',
      Certifications: 'certifications',
      'Témoignages': 'testimonials',
      Contact: 'contact',
    },
    productLinks: { "Huiles d'Olive": '/boutique' },
    supportLinks: {
      FAQ: '/faq',
      Livraison: '/faq#livraison',
      Retours: '/faq#retours',
      Conditions: '/terms',
      Confidentialité: '/privacy-policy',
      'Suppression données': '/data-deletion',
    },
  },
  en: {
    sections: {
      Products: ['Olive Oils'],
      Company: ['Our Products', 'Our Story', 'Certifications', 'Testimonials', 'Contact'],
      Support: ['FAQ', 'Shipping', 'Returns', 'Terms', 'Privacy', 'Data deletion'],
    },
    navAnchors: {
      'Our Products': 'products',
      'Our Story': 'story',
      Certifications: 'certifications',
      Testimonials: 'testimonials',
      Contact: 'contact',
    },
    productLinks: { 'Olive Oils': '/boutique' },
    supportLinks: {
      FAQ: '/faq',
      Shipping: '/faq#livraison',
      Returns: '/faq#retours',
      Terms: '/terms',
      Privacy: '/privacy-policy',
      'Data deletion': '/data-deletion',
    },
  },
}

type LinkMap = Record<string, string>
type SectionMap = Record<string, string[]>

const socialLinks: { icon: typeof Facebook; href: string; label: string }[] = []

export function ModernFooter({ hasTestimonials = false }: { hasTestimonials?: boolean }) {
  const { language, country } = useLanguage()
  const sections = Object.fromEntries(
    Object.entries(linkSets[language].sections).map(([title, links]) => [
      title,
      hasTestimonials ? links : links.filter((link) => link !== 'Témoignages' && link !== 'Testimonials'),
    ])
  ) as SectionMap
  const navAnchors = (
    hasTestimonials
      ? linkSets[language].navAnchors
      : Object.fromEntries(Object.entries(linkSets[language].navAnchors).filter(([link]) => link !== 'Témoignages' && link !== 'Testimonials'))
  ) as LinkMap
  const productLinks = linkSets[language].productLinks as LinkMap
  const supportLinks = linkSets[language].supportLinks as LinkMap
  const copy = language === 'fr' ? fr : en
  const syrisHref = country === 'TN' ? 'https://syris.tn' : 'https://syris.io'
  const rawPhone = copy.footer.contact.phone
  const phoneDisplay = rawPhone

  return (
    <footer className="bg-gradient-to-br from-background via-muted/40 to-background text-foreground">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-12 grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-5">
          <motion.div className="lg:col-span-2" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="mb-6 flex items-center space-x-2">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-card ring-1 ring-border/40">
                <img src="/salamdor.png" alt="Salamdor" className="h-full w-full object-contain" />
              </div>
              <span className="text-xl tracking-tight">Salamdor</span>
            </div>
            <p className="mb-6 max-w-sm leading-relaxed text-muted-foreground">{copy.footer.tagline}</p>
            <div className="space-y-3 text-muted-foreground">
              <a href={`mailto:${copy.footer.contact.email1}`} className="flex items-center space-x-3 hover:text-foreground">
                <Mail className="h-5 w-5" />
                <span>{copy.footer.contact.email1}</span>
              </a>
              <a href={`mailto:${copy.footer.contact.email2}`} className="flex items-center space-x-3 hover:text-foreground">
                <Mail className="h-5 w-5" />
                <span>{copy.footer.contact.email2}</span>
              </a>
              <a href={`tel:${phoneDisplay}`} className="flex items-center space-x-3 hover:text-foreground">
                <Phone className="h-5 w-5" />
                <span>{phoneDisplay}</span>
              </a>
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5" />
                <span>{copy.footer.contact.address}</span>
              </div>
            </div>
          </motion.div>

          {Object.entries(sections).map(([title, links], index) => (
            <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}>
              <h3 className="mb-4 text-foreground">{title}</h3>
              <ul className="space-y-3 text-muted-foreground">
                {links.map((link) => (
                  <li key={link}>
                    <a
                      href={
                        navAnchors[link]
                          ? `/#${navAnchors[link]}`
                          : productLinks[link]
                            ? productLinks[link]
                            : supportLinks[link]
                              ? supportLinks[link]
                            : '#'
                      }
                      className="relative transition-colors hover:text-foreground"
                    >
                      {link}
                      <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div className="border-t border-border/40 pt-8" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }}>
          <div className="flex flex-col items-center justify-between gap-4 text-sm text-muted-foreground md:flex-row">
            <p>
              © {new Date().getFullYear()} Salamdor. {copy.footer.rights} {copy.footer.builtBy}{' '}
              <a href={syrisHref} className="text-primary transition hover:text-primary/80" target="_blank" rel="noreferrer">
                SYRIS L&C
              </a>
              .
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <motion.a key={social.label} href={social.href} aria-label={social.label} className="flex h-10 w-10 items-center justify-center rounded-full bg-muted/60 text-foreground" whileHover={{ scale: 1.1, rotate: 5 }} whileTap={{ scale: 0.95 }}>
                  <social.icon className="h-5 w-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  )
}
